import WarningIcon from '@/assets/icons/warning.svg?react';
import Button from '@/components/ui/button/button.component';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog/dialog.component';
import { useDialogStore } from '@/store/dialog.store';
import { dialogConfirmationTranslations } from './dialog-container.messages';

import { T } from '@/components/i18n/message.i18n.component';
import { cn } from '@/core/string.core';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { dialogLocators } from './dialog.locators';
import { Pill } from '@/components/ui/pill/pill.component';
import { useBusinessUnitStore } from '@/store/business-unit.store';
import { Loader } from '@/components/ui/loader/loader.component';

export const DialogContainer = () => {
  const t = useIntl();
  const { activeBusinessUnit } = useBusinessUnitStore();
  const {
    open,
    title,
    titleTooltip,
    contentLoadingText,
    showActiveBusinessUnit,
    loading,
    contentLoading,
    type,
    item,
    content,
    fullScreen,
    confirmationText,
    confirmButtonText,
    currentFocusTarget,
    lastFocusedElement,
    handleClose,
    handleConfirm,
  } = useDialogStore((state) => ({
    ...state,
    handleClose: state.closeDialog,
    handleConfirm: state.onConfirm,
  }));

  // Global focus restoration for dialogs without explicit focus targets
  useEffect(() => {
    if (!open && !currentFocusTarget && lastFocusedElement) {
      // No specific focus target was set, restore to the last focused element
      lastFocusedElement.focus();
      // Clear the reference to prevent stale focus restoration
      useDialogStore.setState({ lastFocusedElement: null });
    }
  }, [open, currentFocusTarget, lastFocusedElement]);

  const getThemeViaPathname = () => {
    // we're using window.location.pathname and not useLocation hook in this component
    // since this component is used in the root of the app, outside of the Router
    const pathname = window.location.pathname;
    if (pathname.includes('maintainer')) {
      return 'maintainer';
    }
    return 'consumer';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        id={getThemeViaPathname()}
        aria-describedby={undefined}
        className={cn(
          fullScreen && 'min-w-[calc(1200px)] min-h-[calc(100vh-148px)] max-h-[calc(100vh-48px)] overflow-hidden',
        )}
      >
        <DialogTitle
          className={cn(fullScreen ? 'max-w-[calc(720px)]' : 'max-w-[calc(440px)]')}
          data-testid={dialogLocators.dialogTitle}
          titleTooltip={titleTooltip}
        >
          {title || <T {...dialogConfirmationTranslations.confirmTitle} />}
        </DialogTitle>

        {showActiveBusinessUnit && (
          <Pill
            data-testid="dialog-active-business-unit"
            className="typo-primary-500 self-start mb-4 -mt-2"
            text={activeBusinessUnit || ''}
            variant="neutral"
          />
        )}
        {type === 'confirmation' && (
          <>
            <div
              data-testid={dialogLocators.dialogConfirmationDesc}
              className={cn(
                'mb-4 py-4 px-3 typo-secondary flex flex-row items-center rounded gap-2',
                'border border-system-warning',
              )}
            >
              <span>
                <WarningIcon />
              </span>
              <p className="typo-secondary">
                {confirmationText ||
                  t.formatMessage(dialogConfirmationTranslations.confirmDescription, {
                    item: item.name,
                  })}
              </p>
            </div>
            <div className="flex justify-between">
              <Button data-testid={dialogLocators.dialogConfirmationCloseBtn} variant="secondary" onClick={handleClose}>
                <T {...dialogConfirmationTranslations.backButton} />
              </Button>
              <Button data-testid={dialogLocators.dialogConfirmBtn} loading={loading} onClick={handleConfirm}>
                {confirmButtonText || <T {...dialogConfirmationTranslations.confirmButton} />}
              </Button>
            </div>
          </>
        )}
        {type === 'custom' &&
          (contentLoading ? (
            <>
              <div className="flex flex-col items-center justify-center gap-4">
                <Loader fullWidth />
                <p>{contentLoadingText}</p>
              </div>
              <div className="hidden" aria-hidden="true" aria-busy="true">
                {/* Keep mounted while loading so child effects can switch loading off. */}
                {content}
              </div>
            </>
          ) : (
            content
          ))}
      </DialogContent>
    </Dialog>
  );
};
